import { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardStore } from '../../stores/DashboardStore';
import { getUseCaseService } from '../../services/UseCaseService';
import { getRiskReportService } from '../../services/RiskPlusReportService';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';
import type { UseCaseChat } from '../../entities/useCaseChat/UseCaseChat';
import type { UseCaseBrief } from '../../entities/useCaseChat/UseCaseBrief';

type ReportItem = { reportType: string; reportTitle: string; reportId: string };

export interface RiskPlusPageData {
  brief: UseCaseBrief | undefined;
  title: string;
  riskReport: RiskPlusReport | null;
  resolvedChat: UseCaseChat | null;
  useCaseChatReports: {
    impactPlusReport: ReportItem | null;
    riskPlusReport: ReportItem | null;
  } | null;
  isLoadingBriefs: boolean;
  isLoadingReport: boolean;
  reportError: string | null;
  hasRiskReport: boolean;
  showReportsPanel: boolean;
  loadRiskReport: () => Promise<void>;
  refreshReports: () => void;
}

export function useRiskPlusPageData(useCaseChatId: string): RiskPlusPageData {
  const isLoadingBriefs = DashboardStore.useDashboardStore((s) => s.isLoadingUseCaseBriefs);
  const loadUseCaseBriefs = DashboardStore.useDashboardStore((s) => s.loadUseCaseBriefs);
  const useCaseBriefs = DashboardStore.useDashboardStore((s) => s.useCaseBriefs);

  const brief = useMemo(
    () => useCaseBriefs.find((b) => b.useCaseChatId === useCaseChatId),
    [useCaseBriefs, useCaseChatId],
  );

  const title = useMemo(() => {
    if (!brief) return '';
    const t = brief.useCaseTitle?.trim();
    return !t || t === 'N/A' || t === '<untitled>' ? 'Untitled use case' : t;
  }, [brief]);

  const [resolvedChat, setResolvedChat] = useState<UseCaseChat | null>(null);
  const [useCaseChatReports, setUseCaseChatReports] = useState<{
    impactPlusReport: ReportItem | null;
    riskPlusReport: ReportItem | null;
  } | null>(null);
  const [riskReport, setRiskReport] = useState<RiskPlusReport | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  useEffect(() => { void loadUseCaseBriefs(); }, [loadUseCaseBriefs]);

  useEffect(() => {
    if (!useCaseChatId) return;
    void getUseCaseService().getUseCaseById(useCaseChatId)
      .then(setResolvedChat)
      .catch(() => setResolvedChat(null));
  }, [useCaseChatId]);

  useEffect(() => {
    if (!useCaseChatId) return;
    void getUseCaseService().getUseCaseChatReports(useCaseChatId)
      .then(setUseCaseChatReports)
      .catch(() => setUseCaseChatReports(null));
  }, [useCaseChatId]);

  const riskPlusReportId = useCaseChatReports?.riskPlusReport?.reportId ?? null;

  const loadRiskReport = useCallback(async () => {
    if (!riskPlusReportId) {
      setRiskReport(null);
      setReportError(null);
      setIsLoadingReport(false);
      return;
    }
    setReportError(null);
    setIsLoadingReport(true);
    try {
      const report = await getRiskReportService().getRiskReportById(riskPlusReportId);
      setRiskReport(report);
    } catch (err) {
      setRiskReport(null);
      setReportError(err instanceof Error ? err.message : 'Could not load Risk+ report.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [riskPlusReportId]);

  useEffect(() => { void loadRiskReport(); }, [loadRiskReport]);

  const refreshReports = useCallback(() => {
    void getUseCaseService().getUseCaseChatReports(useCaseChatId)
      .then(setUseCaseChatReports)
      .catch(() => setUseCaseChatReports(null));
    void loadUseCaseBriefs();
  }, [useCaseChatId, loadUseCaseBriefs]);

  const hasRiskReport = !!useCaseChatReports?.riskPlusReport;
  const isProcessed = brief?.contentStatus === 'processed' || brief?.contentStatus === 'expertValidated';
  const isSufficient = brief?.contentStatus === 'sufficient';
  const showReportsPanel = isProcessed || isSufficient;

  return {
    brief,
    title,
    riskReport,
    resolvedChat,
    useCaseChatReports,
    isLoadingBriefs,
    isLoadingReport,
    reportError,
    hasRiskReport,
    showReportsPanel,
    loadRiskReport,
    refreshReports,
  };
}
